import { useCallback } from "react";
import {
  edgesAtom,
  addSourceNodeSetter,
  addAndSavePathNodeSetter,
  addTargetNodeSetter,
  togglesAtomResettable,
  addSuggestedTargetNodeSetter,
  hasSuggestedNodesAtom,
  emitEventOnSuggestStepSetter,
} from "@zapier/canvas-state";
import { useAtomValue, useSetAtom } from "jotai";

import { useCenterAndZoomOnNode } from "../../hooks/useCenterAndZoomOnNode";
import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";

import styles from "./AddStepMenu.module.css";

type Props = {
  id: string | null;
  onClick: (event: any) => void;
  handleType?: "target" | "source";
};

function AddStepMenu({ id, onClick, handleType }: Props) {
  const { emitEventOnAddStep, emitEventOnAddPath } =
    useEmitUserInteractionEvents();
  const emitEventOnSuggestStep = useSetAtom(emitEventOnSuggestStepSetter);
  const edges = useAtomValue(edgesAtom);
  const hasOutgoer = edges.find(
    (edge) => edge.source === id && edge.type !== "zapStepEdge",
  );
  const addSourceNode = useSetAtom(addSourceNodeSetter);
  const addTargetNode = useSetAtom(addTargetNodeSetter);
  const { centerAndZoomOnNode } = useCenterAndZoomOnNode();
  const toggles = useAtomValue(togglesAtomResettable);
  const hasSuggestedNodes = useAtomValue(hasSuggestedNodesAtom);
  const suggestNode = useSetAtom(addSuggestedTargetNodeSetter);

  const addPathNodeAndSave = useSetAtom(addAndSavePathNodeSetter);

  const addNodeCb = useCallback(async () => {
    if (!id) {
      return;
    }

    const args = { id, centerAndZoomOnNode };

    if (handleType === "source") {
      await addTargetNode(args);
    } else {
      await addSourceNode(args);
    }
    emitEventOnAddStep();
  }, [
    addTargetNode,
    emitEventOnAddStep,
    addSourceNode,
    handleType,
    id,
    centerAndZoomOnNode,
  ]);

  const handleAddPath = useCallback(() => {
    addPathNodeAndSave({
      sourceNodeId: id,
      nodePartial: { type: "path" },
      numOfPaths: 2,
    });
    emitEventOnAddPath();
  }, [emitEventOnAddPath, addPathNodeAndSave, id]);

  return (
    <div
      className={[
        styles.menu,
        handleType === "source" ? styles.source : styles.target,
      ].join(" ")}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onClick(e);
        }
      }}
      role="button"
      tabIndex={0}
      data-testid={`AddStepMenu_${id}`}
    >
      <button onClick={addNodeCb} data-testid={`AddStepMenu_${id}_addStep`}>
        Add step
      </button>

      {toggles.aiToolbar && !hasSuggestedNodes && (
        <button
          onClick={() => {
            suggestNode(id!);
            emitEventOnSuggestStep();
          }}
        >
          Suggest step
        </button>
      )}
      {!hasOutgoer && handleType !== "target" && (
        <button onClick={() => handleAddPath()}>Split path</button>
      )}
    </div>
  );
}

export default AddStepMenu;
